'use client';

import { MediaPlayer, MediaProvider } from '@vidstack/react';
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from '@vidstack/react/player/layouts/default';
import { useLenis } from 'lenis/react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { z } from 'zod';

import TriangleLeft from '~/assets/icons/triangle--left.svg';
import TriangleRight from '~/assets/icons/triangle--right.svg';
import Media from '~/components/molecules/Media';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';

interface ImagesGalleryClientProps {
  gallery: z.infer<typeof schemas.imagesGalleryLayout>['gallery'];
}

const ImagesGalleryClient: React.FC<ImagesGalleryClientProps> = ({
  gallery,
}) => {
  const { edges } = gallery;

  const lenis = useLenis();

  return (
    <Swiper
      className="pointer-events-none relative z-10 col-span-2 col-start-1 row-start-1 h-full w-full overflow-visible! lg:col-span-12 [&>.swiper-wrapper>.swiper-slide]:w-auto!"
      modules={[Navigation]}
      slidesPerView="auto"
      spaceBetween={8}
      lazyPreloadPrevNext={1}
      grabCursor
      navigation={{
        prevEl: '.swiper-button--prev',
        nextEl: '.swiper-button--next',
      }}
      onSliderMove={() => lenis && lenis.stop()}
      onTouchEnd={() => lenis && lenis.start()}
      onDragEnd={() => lenis && lenis.start()}
    >
      {edges &&
        edges.length > 0 &&
        edges.map(({ node }) => (
          <SwiperSlide key={node.id} className="relative">
            <div className="pointer-events-auto relative h-full w-full bg-white">
              <>
                {node.mediaType === 'image' && (
                  <Media
                    media={{
                      type: 'image',
                      image: { node },
                    }}
                    imageProps={{
                      width: node.mediaDetails.width,
                      height: node.mediaDetails.height,
                      className:
                        'h-80 w-full lg:h-screen lg:w-auto lg:max-h-[calc(100svh-160px)] object-cover max-lg:w-[calc(100svw-16px)]',
                    }}
                  />
                )}

                {node.mediaType === 'file' && (
                  <MediaPlayer
                    className="h-80 w-full rounded-none! border-0! object-cover max-lg:w-[calc(100svw-16px)] lg:h-screen lg:max-h-[calc(100svh-160px)] lg:w-auto lg:max-w-[calc(100svw-192px)]"
                    title={node.altText}
                    src={node.mediaItemUrl}
                  >
                    <MediaProvider className="[&>video]:h-full! [&>video]:w-full! [&>video]:object-cover!" />

                    <DefaultVideoLayout
                      icons={defaultLayoutIcons}
                      colorScheme="light"
                      slots={{ googleCastButton: null }}
                    />
                  </MediaPlayer>
                )}
              </>
            </div>
          </SwiperSlide>
        ))}

      <button
        type="button"
        className="swiper-button--prev text-brand-primary pointer-events-auto absolute top-1/2 left-4 z-20 flex -translate-y-1/2 cursor-pointer items-center justify-center bg-white/40 p-2 backdrop-blur-sm transition-all lg:left-10 [&.swiper-button-disabled]:pointer-events-none [&.swiper-button-disabled]:scale-0 [&.swiper-button-disabled]:opacity-0"
        aria-label="Image précédente"
      >
        <TriangleLeft className="h-6 w-6" />
      </button>

      <button
        type="button"
        className="swiper-button--next text-brand-primary pointer-events-auto absolute top-1/2 right-4 z-20 flex -translate-y-1/2 cursor-pointer items-center justify-center bg-white/40 p-2 backdrop-blur-sm transition-all lg:right-10 [&.swiper-button-disabled]:pointer-events-none [&.swiper-button-disabled]:scale-0 [&.swiper-button-disabled]:opacity-0"
        aria-label="Image suivante"
      >
        <TriangleRight className="h-6 w-6" />
      </button>
    </Swiper>
  );
};

export default ImagesGalleryClient;
